/**
 * Design tokens, lifted from the Claude Design mockups.
 *
 * The system in one line: lime = you, cyan = opponent, amber -> red = danger.
 * Everything else is a dark FPS-HUD chrome.
 */

export const color = {
  // Backgrounds
  bg: '#0a0c0a',
  bgDeep: '#050705',
  bgPanel: 'rgba(0,0,0,.28)',

  // "You" — lime
  lime: '#a6ff2e',
  limeBright: '#c6ff5e',
  limeDeep: '#8ae000',
  limeInk: '#07110a',

  // "Opponent" — cyan
  cyan: '#22e0ff',
  cyanBright: '#7defff',
  cyanInk: '#04222a',

  // Danger ramp
  amber: '#ffb020',
  amberBright: '#ffce6b',
  amberInk: '#1a1204',
  red: '#ff2e4d',
  redBright: '#ff5c74',

  // Text
  text: '#eaffea',
  textDim: 'rgba(234,255,234,.5)',
  textFaint: 'rgba(234,255,234,.4)',
  textGhost: 'rgba(234,255,234,.3)',
} as const;

/** Translucent lime/cyan/amber/red, for borders and glows. */
export const alpha = {
  lime: (a: number) => `rgba(166,255,46,${a})`,
  cyan: (a: number) => `rgba(34,224,255,${a})`,
  amber: (a: number) => `rgba(255,176,32,${a})`,
  red: (a: number) => `rgba(255,46,77,${a})`,
  text: (a: number) => `rgba(234,255,234,${a})`,
  white: (a: number) => `rgba(255,255,255,${a})`,
} as const;

export const font = {
  display: "'Chakra Petch', system-ui, sans-serif",
  mono: "'JetBrains Mono', ui-monospace, monospace",
} as const;

/**
 * The mockups size everything with `clamp(min, Ncqmin, max)` inside a
 * `container-type: size` element, so a screen scales as one unit whether it's
 * 390px or 1120px wide. Reproduced here rather than reinvented with breakpoints.
 */
export const cq = (min: number, vw: number, max: number): string =>
  `clamp(${min}px, ${vw}cqmin, ${max}px)`;

/** Shared full-bleed screen chrome: radial bg, grid, scanlines, corner brackets. */
export const screenShell: React.CSSProperties = {
  containerType: 'size',
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  background: `radial-gradient(120% 120% at 50% 0%,#0d150c 0%,#070b07 58%,${color.bgDeep} 100%)`,
  fontFamily: font.display,
  color: color.text,
  boxSizing: 'border-box',
};
